import React from 'react';
import { StackShim as Stack } from '@fluentui/react-migration-v8-v9';
import { Field, Textarea, FluentProvider, webLightTheme } from '@fluentui/react-components';
import { useQuery } from '@apollo/client';
import { Controller } from 'react-hook-form';
import QueryBasedDropdown from '../../../common/formFields/queryBasedDropdown/QueryBasedDropdown';
import IDropdownReference from '../../../../utils/types/IDropdownReference';
import IFieldsetProps from '../../../../utils/types/IFieldsetProps';
import IAnnualFilingRequirementsFormProps from './IAnnualFilingRequirementsFormProps';
import GET_ACCOUNTING_STANDARDS_QUERY from '../../../../utils/statApi/AccountingStandardsApi';

const AccountingStandardsForm = ({
  annualFilingRequirements,
  form,
}: IAnnualFilingRequirementsFormProps): JSX.Element => {
  const { control, errors, setValue } = form;

  const AccountingStandardsDropdown = (props: IFieldsetProps): JSX.Element => {
    const { value, label, errors: accErrors, handleChange, valueKey, outputKey, required } = props;
    const { loading: accLoading, data: accData } = useQuery(GET_ACCOUNTING_STANDARDS_QUERY);
    const { accountingStandards } = !accLoading && accData ? accData : [];

    return (
      <QueryBasedDropdown
        required={required}
        value={value as IDropdownReference}
        label={label}
        outputKey={outputKey}
        dropdownOptionsData={accountingStandards}
        errors={accErrors}
        handleChange={handleChange}
        valueKey={valueKey}
      />
    );
  };

  return (
    <FluentProvider theme={webLightTheme}>
      <Stack tokens={{ childrenGap: 10 }}>
        <Controller
          render={(props) => (
            <AccountingStandardsDropdown
              label="Accounting Standards"
              valueKey="accountingStandardsUsed"
              value={props.value}
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              handleChange={(newValue: any, valueKey: string): void => {
                setValue('accountingStandardsUsed', newValue);
              }}
              errors={errors}
            />
          )}
          name="accountingStandardsUsed"
          control={control}
          defaultValue={annualFilingRequirements.accountingStandardsUsed}
          value={annualFilingRequirements.accountingStandardsUsed}
        />
        <Controller
          name="accountingStandardsComments"
          control={control}
          defaultValue={annualFilingRequirements.accountingStandardsComments}
          render={({ onChange, value, name }): React.ReactElement => (
            <Field label="Accounting Standards Comments">
              <Textarea
                id="accountingStandardsComments"
                name={name}
                value={value}
                onChange={(e, data): void => {
                  onChange(data.value);
                }}
                resize="vertical"
              />
            </Field>
          )}
        />
        <Controller
          name="ifrsType"
          control={control}
          defaultValue={annualFilingRequirements.ifrsType}
          render={({ onChange, value, name }): React.ReactElement => (
            <Field label="IFRS Type" validationMessage={errors?.ifrsType?.message}>
              <Textarea
                id="ifrsType"
                name={name}
                value={value}
                onChange={(e, data): void => {
                  onChange(data.value);
                }}
                resize="vertical"
              />
            </Field>
          )}
          rules={{
            maxLength: {
              value: 250,
              message: 'Input is greater than the maximum character limit of 250.',
            },
          }}
        />
        <Controller
          name="ifrsSpecialElections"
          control={control}
          defaultValue={annualFilingRequirements.ifrsSpecialElections}
          render={({ onChange, value, name }): React.ReactElement => (
            <Field
              label="IFRS Special Elections"
              validationMessage={errors?.ifrsSpecialElections?.message}
            >
              <Textarea
                id="ifrsSpecialElections"
                name={name}
                value={value}
                onChange={(e, data): void => {
                  onChange(data.value);
                }}
                resize="vertical"
              />
            </Field>
          )}
        />
      </Stack>
    </FluentProvider>
  );
};

export default AccountingStandardsForm;
